import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function UniversitiesCampuses() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Universities & Campuses'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8723" data-mp="1n4g0l14q40wi">
      <Header data-id="element-8724" data-mp="3cil2g6pztwqj" />
      <main className="pt-[120px]" data-id="element-8725" data-mp="3ci30qukkaqb8">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-8726" data-mp="3cgrxcvv13kkn">
          <div className="max-w-3xl mx-auto" data-id="element-8727" data-mp="3hgmlirnf5w20">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8728" data-mp="3hfbjoxdjaydj">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-8729" data-mp="1szum1p9tg69r">
                {isFR ? 'SECTEURS · UNIVERSITÉS ET CAMPUS' : 'SECTORS · UNIVERSITIES & CAMPUSES'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-8730" data-mp="22qgvf1qxw0k0">
                {isFR ? 'Un campus est une mini-ville.' : 'A campus is a mini-city.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-8731" data-mp="1a1s2vr3y93jv">
                {isFR ? 'L’IA arrive vite sur les campus — plus de la moitié des établissements l’utilisent déjà. Mais elle cale là où les données étudiantes vivent dans des dizaines de systèmes déconnectés. La fondation d’abord, l’intelligence ensuite.' : 'AI is arriving on campus fast — over half of institutions already use it. But it stalls where student data lives in dozens of disconnected systems. Foundation first, intelligence second.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-8732" data-mp="1x17hbppeol0u">
                <Link to="/request-demo?industry=universities" onClick={() => track('Demo CTA Clicked', {
                location: 'Universities Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8733" data-mp="2143s4u8pfcu8">
                  {isFR ? 'Demander une démo' : 'Request a demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8734" data-mp="1dbx0g8dxbu15" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8735" data-mp="poyo9mwu7891">
          <div className="max-w-3xl mx-auto" data-id="element-8736" data-mp="pmulx9el6hav">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-8737" data-mp="umqf0dlqwobn">
              {isFR ? 'La recherche montre' : 'Research shows'}
            </motion.div>
            <motion.blockquote {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="border-l-2 border-[#4F46E5] pl-6 mb-8" data-id="element-8738" data-mp="i5pehskf57jg">
              <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug" data-id="element-8739" data-mp="d7xl0u44ldd5">
                {isFR ? 'Le BCG constate que seulement 5 % des organisations captent la valeur qu’elles attendent de l’IA — et que 10 % seulement du succès provient des modèles eux-mêmes. Les 90 % restants, c’est la fondation : les personnes, les processus et des données de confiance. Les universités fonctionnent sur des dizaines de systèmes déconnectés. C’est la fondation qui manque.' : 'BCG finds that only 5% of organizations capture the value they expect from AI — and that just 10% of success comes from the models themselves. The other 90% is foundation: people, processes and trusted data. Universities run on dozens of disconnected systems. The foundation is what’s missing.'}
              </p>
            </motion.blockquote>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-8740" data-mp="1kj5fgfjghic1">
              {isFR ? 'Contexte de marché : il ne s’agit pas de résultats de déploiement BCITI. Source : ' : 'Market context. Not BCITI deployment results. Source: '}
              <a href="https://www.bcg.com/publications/2026/how-ai-can-help-universities-capture-opportunity" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-8741" data-mp="wwfe7gexlrsh">
                {isFR ? 'BCG, « How AI Can Help Higher Education Capture a Once-in-a-Generation Opportunity », mars 2026.' : 'BCG, “How AI Can Help Higher Education Capture a Once-in-a-Generation Opportunity,” March 2026.'}
              </a>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8742" data-mp="2sbvvrjh2pf4u">
          <div className="max-w-3xl mx-auto" data-id="element-8743" data-mp="2s9j4gfpfdsf4">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-8744" data-mp="363sljgv9rcy2">
              {isFR ? 'La carte étudiante, c’est notre Carte numérique BCITI : identité, accès, paiements et services en un. La même fondation qu’une ville — prête pour l’intelligence quand le campus l’est.' : 'The student card is our BCITI Digital Card: identity, access, payments and services in one. The same foundation as a city — ready for intelligence when the campus is.'}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-8745" data-mp="1un2dds7acb1t" />
    </div>;
}